import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/shopify-metafields";
const TITLE = "Shopify Metafelder: Produktdaten sauber pflegen und anzeigen";
const DESCRIPTION =
  "Shopify Metafelder richtig einsetzen: Definitionen anlegen, Produktdaten strukturieren, im Theme ausgeben und als Filter nutzen. Mit Praxisbeispielen und typischen Fehlern.";
const UPDATED = "2026-09-12";

const ANSWER =
  "Metafelder erweitern Produkte, Collections und andere Shopify-Objekte um eigene strukturierte Daten: Material, Pflegehinweise, technische Tabellen, Lieferzeiten. Angelegt werden sie als Definitionen im Admin, gepflegt pro Produkt und im Theme an genau der Stelle ausgegeben, wo Kunden die Information brauchen. Richtig aufgesetzt ersetzen sie Freitext-Wüsten in der Beschreibung und machen Daten filterbar und übersetzbar.";

const faqs = [
  {
    q: "Was sind Metafelder in Shopify?",
    a: "Metafelder sind eigene Datenfelder für Produkte, Varianten, Collections und mehr. Sie werden als Definitionen mit Datentyp angelegt, etwa Text, Zahl, Datei oder Liste, und erscheinen dann als Eingabefelder im Admin.",
  },
  {
    q: "Wie zeige ich Metafelder im Shop an?",
    a: "In modernen Themes lassen sich Metafelder im Editor per dynamischer Quelle verknüpfen. Für komplexere Ausgaben, etwa Tabellen oder bedingte Anzeigen, wird das Metafeld direkt im Theme-Code per Liquid ausgegeben.",
  },
  {
    q: "Kann ich nach Metafeldern filtern?",
    a: "Ja. Metafelder mit passenden Datentypen lassen sich als Filter auf Kategorieseiten aktivieren, etwa nach Material, Farbe oder Größe. Das ersetzt umständliche Tag-Konstrukte.",
  },
  {
    q: "Was ist der Unterschied zu Tags?",
    a: "Tags sind freie Stichworte ohne Struktur und Datentyp. Metafelder sind definierte Felder mit Typ und Validierung: pflegbarer, übersetzbar, filterbar und sauber im Theme ausgebbar.",
  },
  {
    q: "Was kostet die Einrichtung von Metafeldern?",
    a: "Definitionen anlegen ist schnell erledigt. Aufwendiger ist das Theme: Ausgabe, Tabellen, bedingte Logik. Solche Anpassungen rechne ich mit 110 € netto pro Stunde ab, nach Sichtung gibt es einen festen Rahmen.",
  },
];

export const Route = createFileRoute("/ratgeber/shopify-metafields")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      faqs,
      section: "Shopify Entwicklung",
      updated: UPDATED,
    }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Entwicklung"
      title="Shopify Metafelder: das unterschätzte Fundament guter Produktdaten"
      intro="Metafelder entscheiden darüber, ob Produktdaten gepflegt oder versteckt werden. Wer sie sauber aufsetzt, bekommt filterbare Kategorien, konsistente Produktdetails und einen Shop, der mit dem Sortiment mitwächst."
      source="ratgeber-shopify-metafields"
      answer={ANSWER}
      updated={UPDATED}
      sections={[
        {
          h: "Wofür Metafelder gedacht sind",
          p: [
            "Alles, was über Titel, Beschreibung und Preis hinausgeht, gehört in Metafelder: technische Daten, Material, Pflege, Lieferzeit, Downloads, ergänzende Bilder. Die Definition legt Datentyp und Gültigkeit fest, das Admin-Team pflegt Werte pro Produkt.",
            "Der Gewinn liegt in der Struktur: Statt fünf verschiedener Schreibweisen in der Beschreibung gibt es ein Feld mit einheitlichen Werten, das sich filtern, übersetzen und zuverlässig ausgeben lässt.",
          ],
        },
        {
          h: "Typische Anwendungen aus der Praxis",
          p: [
            "Diese Fälle lassen sich mit Metafeldern sauber lösen, ohne eine App zu installieren.",
          ],
          list: [
            "Technische Tabellen auf Produktseiten, einheitlich über das ganze Sortiment",
            "Lieferzeit- und Verfügbarkeitshinweise je Produkt oder Variante",
            "Filter auf Kategorieseiten nach Material, Farbe, Größe oder Einsatzbereich",
            "Produktspezifische Downloads wie Datenblätter oder Montageanleitungen",
            "Bedingte Hinweise, etwa Warnhinweise nur bei bestimmten Produktgruppen",
          ],
        },
        {
          h: "Ausgabe im Theme",
          p: [
            "Einfache Felder verknüpfen moderne Themes im Editor über dynamische Quellen. Sobald die Darstellung komplexer wird, etwa aufklappbare Tabellen, bedingte Abschnitte oder eigene Formatierung, gibt Liquid im Theme den Wert gezielt aus.",
            "Diese Arbeit ist ein klassischer Fall für Theme-Anpassung: einmalig umgesetzt, danach pflegt Ihr Team nur noch die Werte, ohne den Code anzufassen.",
          ],
        },
        {
          h: "Typische Fehler",
          p: [
            "Der häufigste Fehler sind Metafelder ohne Definition und Datentyp, angelegt per App oder Import, die dann im Admin als rohe Textfelder auftauchen. Der zweite sind doppelte Strukturen: dieselbe Information als Tag, als Metafeld und in der Beschreibung.",
            "Beides lässt sich aufräumen: Definitionen mit sauberen Typen nachziehen, Werte per CSV oder API migrieren und die Ausgabe auf eine Quelle reduzieren.",
          ],
        },
        {
          h: "Aufbau in einem Projekt",
          p: [
            "Ich beginne mit einer Bestandsaufnahme der Produktdaten, lege Definitionen und Namensräume fest und baue die Ausgabe im Theme. Umfang abhängig von Sortiment und Darstellung, abgerechnet zu 110 € netto pro Stunde mit vorab festem Rahmen.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/shopify-theme-anpassung", label: "Theme-Anpassung beauftragen" },
        { to: "/ratgeber/shopify-liquid", label: "Shopify Liquid im Überblick" },
        { to: "/ratgeber/shopify-produktseite-optimieren", label: "Produktseite optimieren" },
        { to: "/kontakt", label: "Metafelder-Setup anfragen" },
      ]}
    />
  );
}
